import React, { useState } from 'react';
import {
  Button, FileInput, Icon, v2,
} from '@fieldnation/platform-components';
import styles from './styles.scss';
import { error } from '../legacy/flash-messenger';
import { ACCEPT, FILE_INPUT_HELP_TEXT, DELETE_CONFIRMATION_MESSAGE } from './constants';
import { TrackedPartFile } from './usePartFiles';

const { Modal } = v2;

const formatDate = (iso?: string): string => {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(date).replace(', ', ' at ');
};

interface PhotoFileItemProps {
  file: TrackedPartFile;
  canModify: boolean;
  onPreview: (file: TrackedPartFile) => void;
  onDownload: (file: TrackedPartFile) => void;
  onRequestDelete: (file: TrackedPartFile) => void;
  onRestore: (id: number) => void;
}

const PhotoFileItem = ({
  file,
  canModify,
  onPreview,
  onDownload,
  onRequestDelete,
  onRestore,
}: PhotoFileItemProps): JSX.Element => {
  const isUploading = file.status === 'uploading';
  const isPendingDelete = file.status === 'pending-delete';

  return (
    <div className={styles['photos-file-item']}>
      <div className={styles['photos-file-thumbnail']}>
        <Icon name="image" size={24} />
      </div>
      <div className={styles['photos-file-info']}>
        {isUploading || isPendingDelete ? (
          <p className={styles['photos-file-name']}>{file.fileName}</p>
        ) : (
          <button
            type="button"
            className={styles['photos-file-name']}
            onClick={() => onPreview(file)}
          >
            {file.fileName}
          </button>
        )}
        <p className={styles['photos-file-date']}>
          {isUploading && 'Uploading…'}
          {isPendingDelete && 'Will be removed when you save'}
          {!isUploading && !isPendingDelete && formatDate(file.createdAt)}
        </p>
      </div>
      <div className={styles['photos-file-actions']}>
        {!isUploading && !isPendingDelete && (
          <Button
            icon="download"
            onClick={() => onDownload(file)}
            shouldUseIconComponent
            type="text"
          />
        )}
        {canModify && !isUploading && !isPendingDelete && (
          <Button
            icon="deleteHollow"
            onClick={() => onRequestDelete(file)}
            shouldUseIconComponent
            type="text"
          />
        )}
        {canModify && isPendingDelete && file.id !== undefined && (
          <Button
            label="Undo"
            onClick={() => onRestore(file.id as number)}
            type="text"
          />
        )}
      </div>
    </div>
  );
};

interface PhotosProps {
  files: TrackedPartFile[];
  loading: boolean;
  loadError: boolean;
  /** Provider can upload and delete; buyers get a read-only list. */
  canUpload: boolean;
  canAddMore: boolean;
  onUpload: (files: File[]) => void;
  onRequestDelete: (file: TrackedPartFile) => void;
  onRestore: (id: number) => void;
  getFreshUrl: (id: number) => Promise<string>;
}

const Photos = ({
  files,
  loading,
  loadError,
  canUpload,
  canAddMore,
  onUpload,
  onRequestDelete,
  onRestore,
  getFreshUrl,
}: PhotosProps): JSX.Element => {
  const [previewFile, setPreviewFile] = useState<TrackedPartFile | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [fileToDelete, setFileToDelete] = useState<TrackedPartFile | null>(null);

  // Presigned URLs expire quickly, so mint a fresh one at the moment of preview.
  const openPreview = async (file: TrackedPartFile) => {
    if (file.id === undefined) {
      return;
    }
    setPreviewFile(file);
    setPreviewUrl('');
    try {
      const url = await getFreshUrl(file.id);
      if (!url) {
        throw new Error('No preview URL available.');
      }
      setPreviewUrl(url);
    } catch (err) {
      setPreviewFile(null);
      setPreviewUrl('');
      error((err as Error)?.message || 'Unable to load the photo preview.');
    }
  };

  const closePreview = () => {
    setPreviewFile(null);
    setPreviewUrl('');
  };

  const handleDownload = async (file: TrackedPartFile) => {
    if (file.id === undefined) {
      return;
    }
    try {
      const url = await getFreshUrl(file.id);
      if (!url) {
        throw new Error('No download URL available.');
      }
      const a = document.createElement('a');
      a.href = url;
      a.download = file.fileName;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.click();
    } catch (err) {
      error((err as Error)?.message || 'Unable to download the photo.');
    }
  };

  const confirmDelete = () => {
    if (fileToDelete) {
      onRequestDelete(fileToDelete);
      setFileToDelete(null);
    }
  };

  return (
    <div className={styles['photos-field']}>
      <p className={styles['photos-label']}>Photos</p>

      {canUpload && canAddMore && (
        <FileInput
          helpText={FILE_INPUT_HELP_TEXT}
          accept={ACCEPT}
          multiple
          onDrop={onUpload}
          files={[]}
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onDeleteFile={() => { }}
        />
      )}

      {loading && <p className={styles['photos-file-date']}>Loading photos…</p>}
      {!loading && loadError && (
        <p className={styles['photos-file-date']}>Unable to load photos.</p>
      )}

      {files.map((f) => (
        <PhotoFileItem
          key={f.key}
          file={f}
          canModify={canUpload}
          onPreview={openPreview}
          onDownload={handleDownload}
          onRequestDelete={setFileToDelete}
          onRestore={onRestore}
        />
      ))}

      {previewFile && (
        <Modal
          header="Preview"
          isOpen
          onClose={closePreview}
          size="medium"
          position="default"
          footerActions={[{ label: 'Close', type: 'secondary', onClick: closePreview }]}
        >
          {previewUrl ? (
            <img
              src={previewUrl}
              alt={previewFile.fileName}
              className={styles['photos-preview-image']}
            />
          ) : (
            <p className={styles['photos-file-date']}>Loading…</p>
          )}
        </Modal>
      )}

      {fileToDelete && (
        <Modal
          header="Delete photo"
          isOpen
          onClose={() => setFileToDelete(null)}
          size="small"
          position="default"
          footerActions={[
            { label: 'Cancel', type: 'secondary', onClick: () => setFileToDelete(null) },
            { label: 'Delete', type: 'primary', onClick: confirmDelete },
          ]}
        >
          <p className={styles['photos-delete-message']}>
            {DELETE_CONFIRMATION_MESSAGE}
            <strong>{fileToDelete.fileName}</strong>
            ?
          </p>
        </Modal>
      )}
    </div>
  );
};

export default Photos;
